/** Longest edge requested for gallery grid and card mosaics. */
const THUMB_EDGE = 480;

/**
 * Grid tiles were painting original Uploadcare / ImageKit files. Firefox decodes
 * the full bitmap even when CSS draws it at ~100px, which blows up tab memory.
 * Return a CDN thumbnail for known hosts and leave every other URL unchanged.
 */
export function galleryThumbnailUrl(url: string): string {
  if (!url) {
    return '';
  }

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }

  const host = parsed.hostname.toLowerCase();
  if (host === 'ucarecdn.com' || host.endsWith('.ucarecdn.com') || host.endsWith('.ucarecd.net')) {
    return uploadcareThumb(parsed);
  }
  if (
    host === 'ik.imagekit.io'
    || host.endsWith('.imagekit.io')
    || host === 'images.goodseeker.com'
  ) {
    return imageKitThumb(url);
  }
  return url;
}

function uploadcareThumb(parsed: URL): string {
  const uuid = parsed.pathname.split('/').filter(Boolean)[0];
  if (!uuid || uuid === '-') {
    return parsed.toString();
  }
  return `${parsed.origin}/${uuid}/-/preview/${THUMB_EDGE}x${THUMB_EDGE}/`;
}

function imageKitThumb(raw: string): string {
  const qIndex = raw.indexOf('?');
  const base = qIndex === -1 ? raw : raw.slice(0, qIndex);
  const query = qIndex === -1 ? '' : raw.slice(qIndex + 1);
  const params = query.split('&').filter(Boolean);
  const trParam = params.find(part => part.startsWith('tr='));
  const trValue = trParam ? decodeURIComponent(trParam.slice(3)) : '';
  if (isSmallImageKitTransform(trValue)) {
    return raw;
  }
  const next = params.filter(part => !part.startsWith('tr='));
  next.push(`tr=w-${THUMB_EDGE}`);
  return `${base}?${next.join('&')}`;
}

function isSmallImageKitTransform(tr: string): boolean {
  if (!tr || tr.indexOf('n-full_size') !== -1) {
    return false;
  }
  if (tr.indexOf('n-stories_list') !== -1 || tr.indexOf('n-logo') !== -1) {
    return true;
  }
  const width = /(?:^|,)w-(\d+)/.exec(tr);
  const height = /(?:^|,)h-(\d+)/.exec(tr);
  const w = width ? Number(width[1]) : null;
  const h = height ? Number(height[1]) : null;
  if (w != null && w <= THUMB_EDGE && (h == null || h <= THUMB_EDGE)) {
    return true;
  }
  if (h != null && h <= THUMB_EDGE && w == null) {
    return true;
  }
  return false;
}
